import { Dialog } from '@progress/kendo-react-dialogs';
import {
  Card,
  CardBody,
  CardHandle,
  CardHeader,
  CardSubtitle,
  CardTitle,
} from '@progress/kendo-react-layout';
import { ResetPasswordPage } from 'app/pages/ResetPasswordPage';
import ClickOutside from 'app/widgets/ClickOutside';
import {
  chevron_right as ChevronRight,
  ic_account_language as IconLanguage,
  ic_account_page as IconLegalTerms,
  ic_account_logout as IconLogout,
  ic_account_password as IconPassword,
  ic_account_settings as IconSettings,
  ic_account_support as IconSupport,
} from 'app/widgets/SVGs';
import * as React from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { Avatar } from 'app/components/Avatar';
import { Tooltip } from 'app/components/Tooltip';
import { ConfirmationDialog } from 'app/components/dialogs';
import { useRequireUserNames } from 'app/components/dialogs/SetUserNamesDialog';
import { DiamondIcon, QuestionIcon } from 'app/components/icons';
import useOAuthProviderInfo from 'app/hooks/useAuthProviderInfo';
import useFlag from 'app/hooks/useFlag';
import { useGetUserInfoQuery } from 'app/hooks/user-info';
import useUsername from 'app/hooks/user-info/useUsername';
import { chevron_down as DownArrow } from 'app/widgets/SVGs';
import { useFullscreenStripeLoader, useLanguageContext } from 'context';
import { getRegion } from 'locales/utils';
import _ from 'lodash';
import { changePasswordRequest, msalInstance } from 'msalConfig/msalConfig';
import { authService } from 'services/AuthService';
import { axiosInstance } from 'services/api';
import { subscriptionsApi } from 'services/api/subscription/queries';
import { changeAppLanguage } from 'services/slices/locale';
import { useAppDispatch } from 'services/store';
import { FeatureName } from 'types/entities/MobileSettings';
import {
  getContactUsUrl,
  getFrequentlyAskedQuestionsUrl,
  getManageFinancingUrl,
} from 'utils/link-urls';
import capitalizeFirstLetter from 'utils/text/capitalizeFirstLetter';
import { GlobalErrorContext } from '../ReactContexts/globalErrorContext';
import { LayoutContext } from '../ReactContexts/layoutContext';
import { SignOutDialog } from './SignOutDialog';
import './SignOutDialog.less';
import './SingedInComponent.less';

export function SingedInComponent() {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();

  const { language, changeLanguage } = useLanguageContext();

  const navigate = useNavigate();
  const region = getRegion();

  const username = useUsername();
  const { data: user } = useGetUserInfoQuery();
  const { requireUserNames } = useRequireUserNames();

  const isSingleSignOnFeatureEnabled = useFlag(FeatureName.SINGLE_SIGN_ON);
  const isSubscriptionsFeatureEnabled = useFlag(FeatureName.SUBSCRIPTIONS);

  const { isAuthenticatedUsingOAuth, provider } = useOAuthProviderInfo();

  const [isLanguageExpanded, setIsLanguageExpanded] = React.useState(false);
  const [isExpanded, setIsExpanded] = React.useState(false);
  const [showResetPasswordDialog, setShowResetPasswordDialog] =
    React.useState<boolean>(false);
  const [
    showUnableToChangePasswordDialog,
    setShowUnableToChangePasswordDialog,
  ] = React.useState(false);

  const [showSignoutDialog, setShowSignoutDialog] = React.useState(false);
  const { classNames, removeSpecificClass } = React.useContext(LayoutContext);
  const { addErrorModal } = React.useContext(GlobalErrorContext);

  const { show: showStripeLoader, hide: hideStripeLoader } =
    useFullscreenStripeLoader();

  const cardRef = React.useRef(null as null | CardHandle);

  var contactUsUrl = getContactUsUrl();

  const [
    createCustomerPortalSession,
    { isLoading: isCreatingCustomerPortalSession },
  ] = subscriptionsApi.useCreateCustomerPortalSessionMutation();

  const handleClick = () => {
    if (_.some(classNames, cls => cls.class === 'mobile-nav-shown')) {
      removeSpecificClass('mobile-nav-shown');
    }
  };

  React.useEffect(() => {
    function handleClickOutside(event) {
      if (event.target.className === 'k-overlay') {
        event.stopPropagation();
        setShowResetPasswordDialog(false);
        setShowSignoutDialog(false);
      }
    }

    document.addEventListener('click', handleClickOutside);
    return () => {
      document.removeEventListener('click', handleClickOutside);
    };
  });

  const onNavigateClicked = (item: string) => {
    setIsExpanded(false);
    setIsLanguageExpanded(false);
    switch (item) {
      case 'setting':
        navigate('/settings');
        break;
      case 'password':
        setShowResetPasswordDialog(true);
        break;
      case 'language':
        const shouldExpand = !isLanguageExpanded;
        setIsExpanded(shouldExpand);
        setIsLanguageExpanded(shouldExpand);
        break;
      case 'report':
        window.open(contactUsUrl, '_blank');
        break;
      case 'legalTerms':
        navigate('/legalTerms');
        break;
      case 'signOut':
        setShowSignoutDialog(true);
        break;
      case 'manage-financing': {
        const href = getManageFinancingUrl();
        window.open(href, '_blank');
        break;
      }
      case 'frequently-asked-questions': {
        const href = getFrequentlyAskedQuestionsUrl();
        window.open(href, '_blank');
        break;
      }
    }
  };

  const b2cChangePasswordClicked = async () => {
    try {
      await msalInstance.loginRedirect(changePasswordRequest(language));
    } catch (ex) {
      addErrorModal();
    }
  };

  const handleChangePassword = async () => {
    if (isAuthenticatedUsingOAuth) {
      setShowUnableToChangePasswordDialog(true);
      setIsExpanded(false);
    } else if (!isSingleSignOnFeatureEnabled) {
      onNavigateClicked('password');
    } else {
      b2cChangePasswordClicked();
    }
  };

  const handleSignOut = async () => {
    const deviceId = window.localStorage.getItem('kubota_deviceId');
    if (deviceId) {
      await axiosInstance
        .delete(`/notification/fcm-token/${deviceId}`)
        .then(result => {
          if (result.status == 200) {
            console.log('deleted succesfully');
            window.localStorage.removeItem('kubota_deviceId');
          }
        })
        .catch(ex => {
          addErrorModal();
        });
    }
    await authService.logout();
  };

  const handleLanguageClickOutside = event => {
    const targetNode = event.target as Node;
    if (cardRef.current && !cardRef.current.element?.contains(targetNode)) {
      setIsExpanded(false);
    }
    setIsLanguageExpanded(false);
  };

  async function handleOpenCustomerPortal() {
    showStripeLoader();

    try {
      const { sessionUrl } = await createCustomerPortalSession({
        returnUrl: window.location.href,
      }).unwrap();

      window.location.href = sessionUrl;
    } catch (error) {
      hideStripeLoader();
      addErrorModal();
    }
  }

  return (
    <>
      <ConfirmationDialog
        open={showUnableToChangePasswordDialog && isAuthenticatedUsingOAuth}
        title={t(
          'profile_menu.change_password.dialogs.unable_to_change_password.title',
          { provider: provider && capitalizeFirstLetter(provider) },
        )}
        confirmationLabel={t('ok')}
        onConfirm={() => setShowUnableToChangePasswordDialog(false)}
      >
        {t(
          'profile_menu.change_password.dialogs.unable_to_change_password.subtitle',
          { provider: provider && capitalizeFirstLetter(provider) },
        )}
      </ConfirmationDialog>

      {showResetPasswordDialog && (
        <Dialog className="modal-form">
          <ResetPasswordPage close={() => setShowResetPasswordDialog(false)} />
        </Dialog>
      )}
      {showSignoutDialog && (
        <Dialog className="sign-out-dialog modal-center modal-remove-item modal-fulltext">
          <SignOutDialog
            close={() => setShowSignoutDialog(false)}
            signOut={handleSignOut}
          />
        </Dialog>
      )}
      <ClickOutside
        onClickOutside={() => {
          setIsExpanded(false);
        }}
      >
        <>
          <span
            className="account-actions"
            onClick={() => {
              setIsExpanded(!isExpanded);
              handleClick();
            }}
          >
            <Tooltip title={username} className="username-text">
              {username}
            </Tooltip>

            {user && (
              <Avatar
                className="singed-in-component__avatar"
                email={user?.email}
                firstName={user?.first_name}
                lastName={user?.last_name}
              />
            )}

            <span className="avatar-down-arrow">
              <DownArrow />
            </span>
          </span>
          {isExpanded && (
            <Card
              className="top-card top-card-account-settings top-card-account-settings-logged-in"
              ref={cardRef}
            >
              <CardHeader>
                <CardTitle>{t('welcome_message')}</CardTitle>
                <CardSubtitle>
                  <Tooltip title={username}>{username ?? ''}</Tooltip>
                </CardSubtitle>
              </CardHeader>
              <CardBody>
                <div>
                  <ul className="k-reset k-header k-menu k-menu-vertical">
                    <li className="k-item k-menu-item k-first">
                      <span
                        className="k-link k-menu-link"
                        onClick={() => {
                          onNavigateClicked('setting');
                        }}
                      >
                        <div>
                          <span>
                            <span className="link-icon">
                              <IconSettings />
                            </span>
                            <span className="link-text">
                              {t('account_and_settings')}
                            </span>
                          </span>
                          <span className="link-arrow">
                            <ChevronRight />
                          </span>
                        </div>
                      </span>
                    </li>

                    <li className="k-item k-menu-item">
                      <span
                        className="k-link k-menu-link"
                        onClick={handleChangePassword}
                      >
                        <div>
                          <span>
                            <span className="link-icon">
                              <IconPassword />
                            </span>
                            <span className="link-text">
                              {t('change_password_preference_title')}
                            </span>
                          </span>
                          <span className="link-arrow">
                            <ChevronRight />
                          </span>
                        </div>
                      </span>
                    </li>

                    {isSubscriptionsFeatureEnabled && (
                      <li className="k-item k-menu-item">
                        <span
                          className="k-link k-menu-link"
                          onClick={() => {
                            setIsExpanded(false);
                            if (!isCreatingCustomerPortalSession) {
                              if (!requireUserNames()) {
                                return;
                              }

                              handleOpenCustomerPortal();
                            }
                          }}
                        >
                          <div>
                            <span>
                              <span className="link-icon">
                                <DiamondIcon fill="#909090" />
                              </span>
                              <span className="link-text">
                                {t('appbar_menu.manage_subscriptions')}
                              </span>
                            </span>
                            <span className="link-arrow">
                              <ChevronRight />
                            </span>
                          </div>
                        </span>
                      </li>
                    )}

                    <li className="k-item k-menu-item">
                      <ClickOutside onClickOutside={handleLanguageClickOutside}>
                        <span
                          className="k-link k-menu-link"
                          onClick={() => {
                            onNavigateClicked('language');
                          }}
                        >
                          <div>
                            <span>
                              <span className="link-icon">
                                <IconLanguage />
                              </span>
                              <span className="link-text">
                                <div className="language-component language-menu-dropdown">
                                  <span>
                                    <span>{t('language')}</span>
                                    <span className="selected-language">
                                      {language.toLocaleUpperCase().slice(0, 2)}
                                    </span>
                                  </span>
                                  {isLanguageExpanded && (
                                    <Card className="top-card top-card-language-setting language-menu-dropdown-options">
                                      <div className="">
                                        <span
                                          onClick={() => {
                                            changeLanguage('en');
                                            dispatch(changeAppLanguage('en'));
                                          }}
                                        >
                                          {t('language_EN')}
                                        </span>
                                        <span
                                          onClick={() => {
                                            changeLanguage('fr');
                                            dispatch(changeAppLanguage('fr'));
                                          }}
                                        >
                                          {t('language_FR')}
                                        </span>
                                      </div>
                                    </Card>
                                  )}
                                </div>
                              </span>
                            </span>
                            <span className="link-arrow">
                              <ChevronRight />
                            </span>
                          </div>
                        </span>
                      </ClickOutside>
                    </li>

                    {isSubscriptionsFeatureEnabled && (
                      <li className="k-item k-menu-item">
                        <span
                          className="k-link k-menu-link"
                          onClick={() => {
                            onNavigateClicked('frequently-asked-questions');
                          }}
                        >
                          <div>
                            <span>
                              <span className="link-icon">
                                <QuestionIcon width="24" height="24" />
                              </span>
                              <span className="link-text">
                                {t('appbar_menu.frequently_asked_questions')}
                              </span>
                            </span>
                            <span className="link-arrow">
                              <ChevronRight />
                            </span>
                          </div>
                        </span>
                      </li>
                    )}

                    <li className="k-item k-menu-item">
                      <span
                        className="k-link k-menu-link"
                        onClick={() => {
                          onNavigateClicked('report');
                        }}
                      >
                        <div>
                          <span>
                            <span className="link-icon">
                              <IconSupport />
                            </span>
                            <span className="link-text">
                              {t('kubota_report_problem')}
                            </span>
                          </span>
                          <span className="link-arrow">
                            <ChevronRight />
                          </span>
                        </div>
                      </span>
                    </li>

                    {region === 'US' && (
                      <li className="k-item k-menu-item">
                        <span
                          className="k-link k-menu-link"
                          onClick={() => {
                            onNavigateClicked('manage-financing');
                          }}
                        >
                          <div>
                            <span>
                              <span className="link-icon">
                                <IconLanguage />
                              </span>
                              <span className="link-text">
                                {t('manage_financing')}
                              </span>
                            </span>
                            <span className="link-arrow">
                              <ChevronRight />
                            </span>
                          </div>
                        </span>
                      </li>
                    )}

                    <li className="k-item k-menu-item">
                      <span
                        className="k-link k-menu-link"
                        onClick={() => {
                          onNavigateClicked('legalTerms');
                        }}
                      >
                        <div>
                          <span>
                            <span className="link-icon">
                              <IconLegalTerms />
                            </span>
                            <span className="link-text">
                              {t('legal_terms_fragment_title')}
                            </span>
                          </span>
                          <span className="link-arrow">
                            <ChevronRight />
                          </span>
                        </div>
                      </span>
                    </li>

                    <li className="k-item k-menu-item k-last">
                      <span
                        className="k-link k-menu-link"
                        onClick={() => {
                          onNavigateClicked('signOut');
                        }}
                      >
                        <div>
                          <span>
                            <span className="link-icon">
                              <IconLogout />
                            </span>
                            <span className="link-text">
                              {t('sign_out_title')}
                            </span>
                          </span>
                          <span className="link-arrow">
                            <ChevronRight />
                          </span>
                        </div>
                      </span>
                    </li>
                  </ul>
                </div>
              </CardBody>
            </Card>
          )}
        </>
      </ClickOutside>
    </>
  );
}
